import React, { useState, useEffect, useMemo,useCallback } from 'react';
import { ThemeProvider, useTheme } from 'styled-components';
import inx from 'inx';
import filesize from 'filesize';
import PropTypes from 'prop-types';
import { ATTACHMENTS } from 'newJs/config';
import { AttachmentsContainer } from './Component/styles';
import { ButtonPane, InfoPane, Toolbar } from './Toolbar';
import { getImgIds } from '../utils';
import { useGlobalProps } from 'providers/global-props';
import AttachmentsPanel from 'containers/Attachments';
import AttachmentsPrint from 'containers/Attachments/List/Print';
import AttachsParamsProvider from 'providers/attachs-params';
import AttachsStateProvider from 'providers/attachs-state';
import PreviewPanels from './PreviewPanels';
import ToggleMore from 'containers/misc/MoreIndicator';
import useMessageStore from 'newJs/state/messageState';
import theme from 'newJs/config/theme';
import {useSearchParams} from 'react-router-dom';
import {useMailListStore} from 'state/mailListState';
const totalAttachSize = (attachments) =>
    filesize(attachments.reduce((total, { size }) => total + size, 0), { round: 2, standard: 'jedec', base: 2 });

const useAttachmentsState = (storeAttachments, gridColsCount, i18nAttachmentsName) => {
    const [attachments, setAttachments] = useState(storeAttachments || []);
    const [isOpen, setIsOpen] = useState(false);
    const attachsCount = attachments.length;
    const visibleAttachCount = useMemo(() => Math.min(gridColsCount, attachsCount), [gridColsCount, attachsCount]);
    const attachsInfo = useMemo(() => (
        `${i18nAttachmentsName}: ${attachsCount} (${totalAttachSize(attachments)})`
    ), [i18nAttachmentsName, attachsCount, attachments]);

    return {
        attachments,
        setAttachments,
        attachsCount,
        visibleAttachCount,
        attachsInfo,
        isOpen,
        setIsOpen,
    };
};

export default function Attachments() {
    const { MAPI, i18n, locale } = useGlobalProps();
    const { breakpoint } = useTheme();
    const [searchParams] = useSearchParams();
    const { apiHost } = inx.mail.params.message;
    const selectors = useMailListStore((state) => state.msgSelectors);
    const gridColsCount = ATTACHMENTS.GRID[breakpoint.name].cols;
    const emlData = useMessageStore((state) => state.emlData);
    const storeAttachments = emlData.attachments;
    const isPrintPreview = useMessageStore((state) => state.isPrintPreview);
    const msgUID = searchParams.get('index');
    const currentFolder = searchParams.get('mailbox') || 'INBOX';
    const {
        attachments,
        setAttachments,
        attachsCount,
        visibleAttachCount,
        attachsInfo,
        isOpen,
        setIsOpen
    } = useAttachmentsState(storeAttachments, gridColsCount, i18n.attachments.name);
    const [limit, setLimit] = useState(visibleAttachCount);
    const providerDefaults = useMemo(() => ({
        params: {
            apiHost,
            currentFolder,
            locale,
            MAPI,
            msgUID,
            selectors,
            i18n: i18n.attachments,
            tooltipComponentId: 'tooltip-for-attachments',
        },
        state: {
            currentBreakpoint: {
                data: ATTACHMENTS.GRID[breakpoint.name],
                name: breakpoint.name,
            },
            imgIdsList: getImgIds(attachments),
        },
    }), [apiHost, currentFolder, locale, MAPI, msgUID, selectors, i18n.attachments, breakpoint.name, attachments]);

    useEffect(() => {
        if (storeAttachments) {
            setAttachments(storeAttachments);
            setLimit(visibleAttachCount);
        }
    }, [storeAttachments, setAttachments, visibleAttachCount]);

    const toggleAllAttachs = useCallback((bool) => {
        setLimit(bool ? attachsCount : visibleAttachCount);
        setIsOpen(bool);
    }, [attachsCount, setIsOpen, visibleAttachCount]);

    const triggerToggle = useCallback(() => {
        setLimit(!isOpen ? attachsCount : visibleAttachCount);
        setIsOpen((prev) => !prev);
    },[attachsCount, isOpen, setIsOpen, visibleAttachCount]);

    return (
        <ThemeProvider theme={theme}>
            {/* FIXME theme is prop is added only because it is required */}
            <AttachsParamsProvider value={providerDefaults.params}>
                <AttachsStateProvider value={providerDefaults.state}>
                    <AttachmentsContainer id={selectors.emlAttachments}>
                        <Toolbar id={`${selectors.emlAttachments}-toolbar`}>
                            {isPrintPreview ? (
                                attachsInfo
                            ) : (
                                <>
                                    <InfoPane>
                                        {attachsCount > gridColsCount ? (
                                            <ToggleMore
                                                className="more-toggler more-toggler_attachments"
                                                direction="right bottom"
                                                isOpen={isOpen}
                                                onToggle={toggleAllAttachs}
                                            >
                                                {attachsInfo}
                                            </ToggleMore>
                                        ) : (
                                            attachsInfo
                                        )}
                                    </InfoPane>
                                    <ButtonPane i18n={i18n.attachments} />
                                </>
                            )}
                        </Toolbar>
                        {isPrintPreview ? (
                            <AttachmentsPrint list={attachments} />
                        ) : (
                            <>
                                <AttachmentsPanel
                                    attachsCountTotal={attachsCount}
                                    list={attachments.slice(0, limit)}
                                    toggleAllAttachs={triggerToggle}
                                    $isOpen={isOpen}
                                />
                                <PreviewPanels />
                            </>
                        )}
                    </AttachmentsContainer>
                </AttachsStateProvider>
            </AttachsParamsProvider>
        </ThemeProvider>
    );
}

Attachments.propTypes = {
    apiHost: PropTypes.object,
    msgUID: PropTypes.string,
    currentFolder: PropTypes.string,
    selectors: PropTypes.object,
};